import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import React, { useEffect, useMemo, useState } from "react";
import { Text } from "@pages/CCP/components/Text";
import { themeDefault } from "src/theme-revamp";
import Button from "@components/_Revamp/Button";
import Contactus from "@pages/CCP/components/Contactus";
import Icon from "@components/_Revamp/Icon";
import HealthMainContainer from "@components/_Revamp/HealthMainContainer";
import { HOME_URL } from "@static/config";
import { clearCCPStore } from "@pages/CCP/Helpers/helpers";
import { store } from "@redux/store";
import { cloneDeep } from "lodash";
import { SCREEN_SIZE } from "@static/screen-size";
import { mask } from "@utils/mask";
import { Helpers, LoadingScreen } from "@utils/helpers";
import { getPolicyNumberCCP } from "@api/index";
import {
  trackOnLoad,
  trackOnSuccess,
} from "@pages/CCP/Helpers/analyticsHelper";
import { AA } from "@utils/adobeAnalyticsSS";
import { CCPDataLayer } from "@pages/CCP/DataLayer/Datalayer.model";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { useDispatch } from "react-redux";
import { updatePaymentData } from "@redux/slices/CCP/step6Slice";

const ThankyouForm = () => {
  const [planSummary, setPlanSummary] = useState<any>();
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [policyNumber, setPolicyNumber] = useState<string>();
  const urlParams: URLSearchParams = Helpers.getURLSearchParams();
  const history = useHistory();
  const dispatch = useDispatch();

  let cancerCoverStep1 = cloneDeep(store.getState().cancerCoverStep1);
  let cancerCoverStep4 = cloneDeep(store.getState().cancerCoverStep4);
  let cancerCoverStep6 = cloneDeep(store.getState().cancerCoverStep6);

  const infoData = [
    {
      num: "1",
      text: "Your application will be processed within the next 24 hours.",
    },
    {
      num: "2",
      text: "If everything is in order, your policy activates and coverage starts.",
    },
    {
      num: "3",
      text: `Stay tuned for a confirmation SMS.`,
    },
  ];

  const planSummaryData = useMemo(() => {
    return [
      {
        heading: {
          text: "Policy Number",
          value: policyNumber || cancerCoverStep6?.payment?.policyNumber,
        },
      },
      {
        heading: {
          text: "Plan",
          value: "Singlife Cancer Cover Plus II",
        },
      },
      {
        heading: {
          text: "Life Assured",
          value:
            cancerCoverStep1?.basicInfo?.forwhom === "child"
              ? cancerCoverStep4.selectedChildInfo?.fullName
              : cancerCoverStep4.myInfo.fullName,
        },
      },
      {
        heading: {
          text: "Premium Payable",
          value: Helpers.toCurrency(
            cancerCoverStep1?.quotation?.totalPremium || 0
          ),
        },
      },
      {
        heading: {
          text: "Premium Frequency",
          value:
            cancerCoverStep1?.basicInfo?.paymentOption === "AP"
              ? "Annual"
              : "Monthly",
        },
      },
      {
        heading: {
          text: "Premium Payment Mode",
          value: "Credit Card",
        },
      },
    ];
  }, [
    cancerCoverStep1?.basicInfo?.forwhom,
    cancerCoverStep1?.basicInfo?.paymentOption,
    cancerCoverStep1?.quotation?.totalPremium,
    cancerCoverStep4.myInfo.fullName,
    cancerCoverStep4.selectedChildInfo?.fullName,
    cancerCoverStep6?.payment?.policyNumber,
    policyNumber,
  ]);

  const goToMainWebsite = () => {
    window.open(HOME_URL, "_self");
  };

  const detectScreenWidth = () => {
    setScreenSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", detectScreenWidth);
    return () => {
      window.removeEventListener("resize", detectScreenWidth);
    };
  }, [screenSize]);

  useEffect(() => {
    if (planSummary === undefined && policyNumber !== undefined) {
      setPlanSummary(planSummaryData);
    } else {
      if (planSummary !== undefined && policyNumber !== undefined)
        clearCCPStore();
    }
  }, [planSummary, planSummaryData, policyNumber]);

  useEffect(() => {
    async function fetchPolicyNumber() {
      LoadingScreen();
      try {
        if (!cancerCoverStep6?.referenceNumber) return;
        const { data } = await getPolicyNumberCCP(
          cancerCoverStep6?.referenceNumber
        );
        const { policyNumber } = data;
        const aadata = AA.getInstance().digitalData as CCPDataLayer;
        trackOnLoad({
          acquisitionUpdated: {
            ...aadata.acquisition,
            product: {
              ...aadata.acquisition.product,
              policyID: policyNumber,
            },
          },
        });
        trackOnSuccess({
          acquisitionUpdated: {
            ...aadata.acquisition,
            product: {
              ...aadata.acquisition.product,
              policyID: policyNumber,
            },
          },
        });
        setPolicyNumber(policyNumber);
      } catch {
        setPolicyNumber("");
      } finally {
        LoadingScreen(false);
      }
    }

    fetchPolicyNumber();
  }, []);

  useEffect(() => {
    const status: string = urlParams.get("status") || "";
    if (status === "unsuccessful") {
      dispatch(updatePaymentData({ status: "unsuccessful" }));
      history.push(`${ROUTE_PATH.CANCER_COVER_REVIEW}`);
    }
  }, [dispatch, history, planSummary, urlParams]);

  return (
    <>
      <HealthMainContainer stepList={[]}>
        <FormContainer>
          <FormHeaderContainer>
            <FormHeaderTitleContainer marginHorizontal={"130px"}>
              <Text variant="title" theme={themeDefault} align="center">
                Application Submitted!
              </Text>
            </FormHeaderTitleContainer>
            <FormHeaderDescriptionContainer>
              <Text
                variant="title"
                theme={themeDefault}
                textClass={HeaderDescriptionCss}
              >
                We've received your payment. Your policy application details
                will be emailed to you shortly. Here’s what to expect next.
              </Text>
            </FormHeaderDescriptionContainer>
          </FormHeaderContainer>
        </FormContainer>

        <FormContentContainer>
          <ULArea>
            {infoData.map((item: any, i: number) => (
              <LIArea key={i} className={item.style}>
                <NumberArea>{item.num}</NumberArea>
                <Text variant="base">{item.text}</Text>
              </LIArea>
            ))}
          </ULArea>
        </FormContentContainer>
        {cancerCoverStep6?.financialAdviser?.autoAssignAgent ===
        "own-adviser" ? (
          <InfoContainer>
            <Icon name="evergreen-info-black-filled" className="icon-info" />
            <Text variant="description">
              <span className="name">
                {mask(
                  cancerCoverStep6.financialAdviser.adviserData.data
                    ?.fullName || ""
                )}
              </span>{" "}
              {`will be your servicing adviser to help you with any queries.`}
            </Text>
          </InfoContainer>
        ) : null}
        <SectionContainer>
          <ListingContainer margin={"0"}>
            <Text
              variant="title"
              margin="0px 0px 50px 0px"
              color={themeDefault.colors.primary}
            >
              Plan Summary
            </Text>
            {planSummary?.map((entries: any) => {
              return (
                <PolicyConfirmationContainer>
                  <span>{entries.heading.text}</span>
                  <span>{entries.heading.value}</span>
                </PolicyConfirmationContainer>
              );
            })}
          </ListingContainer>
        </SectionContainer>

        <FormButtonContainer>
          <Button
            onClick={goToMainWebsite}
            width={"300px"}
            variant={"default"}
            theme={themeDefault}
            label={"Back to Home"}
          ></Button>
        </FormButtonContainer>
        <Contactus
          descriptionClass={descriptionClass}
          findoutLink={{ default: 14, tablet: 14 }}
          color={themeDefault.colors.primary}
          cardLinkClass={{ default: 14, tablet: 14 }}
          cardInfoHeadingClass={font18}
        />
      </HealthMainContainer>
    </>
  );
};

export default ThankyouForm;

const SectionContainer = styled.div<{ margin?: string; width?: string }>`
  background-color: #ffffff;
  border-radius: 24px;
  opacity: 1;
  -webkit-transition: opacity 0.5s ease-in;
  -moz-transition: opacity 0.5s ease-in;
  -o-transition: opacity 0.5s ease-in;
  -ms-transition: opacity 0.5s ease-in;
  transition: opacity 0.5s ease-in;
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  transition: all 0.2s ease;
  margin: 0px auto auto;
  padding: 24px 16px;
  scroll-margin-top: 150px;
  white-space: break-spaces;

  & div:last-child {
    border-bottom: 0;
  }
  &[data-id="interbank"] {
    margin-top: 24px;
  }

  @media screen and (min-width: 768px) {
    max-width: 588px;
    margin: auto;
    padding: 24px;
  }
  @media screen and (max-width: 768px) {
    // margin: auto 24px;
  }
  @media screen and (min-width: 1024px) {
    max-width: 792px;
  }

  @media screen and (max-width: 588px) {
    // margin: auto 24px;
  }
`;

const FormContainer = styled.div<FormContainerProps>`
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 auto;
`;

const ListingContainer = styled.div<FormContainerProps>`
  width: 100%;
  margin: 0 auto;
  & div:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
  @media screen and (min-width: 768px) {
    max-width: 792px;
    margin: ${(props) => (props.margin ? props.margin : "150px auto 0 auto")};
  }
  @media screen and (min-width: 1024px) {
    max-width: 792px;
  }
  @media screen and (max-width: 588px) {
    max-width: 327px;
  }
`;

const FormHeaderContainer = styled.div`
  display: block;
  margin-bottom: 36px;
  padding: 0px 10px;
  @media screen and (min-width: 768px) {
    margin-bottom: 48px;
  }
  @media screen and (max-widh: 588px) {
    max-width: 327px;
  }
`;

const FormContentContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: column;
  column-gap: 30px;
  margin-bottom: 48px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    margin-bottom: 36px;
  }
`;

const FormHeaderTitleContainer = styled.div<FormHeaderTitleContainerProps>`
  display: block;
  /* margin: 0 ${(props) => props.marginHorizontal}; */
  margin-bottom: 16px;
  width: 100%;
  @media screen and (max-width: 768px) {
    /* padding: 0 20px;
    margin: 0 20px 16px 20px;
    width: 327px; */
  }
`;

const FormHeaderDescriptionContainer = styled.div`
  display: block;
  margin: 0 50px;
  @media screen and (max-width: 768px) {
    margin: 0;
    padding: 0;
  }
`;

const FormButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 64px;
  column-gap: 40px;
  @media screen and (max-width: 767px) {
    column-gap: 0;
    row-gap: 10px;
    margin: 24px auto 0;
    flex-direction: column;
    align-items: center;
    max-width: 327px;
    width: 100%;
  }
`;

const HeaderDescriptionCss = css`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  color: #8a8988;
  @media screen and (min-width: 768px) {
    // font-weight: 600;
  }
`;

// stems area
const ULArea = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  isolation: isolate;
  p {
    text-align: center;
  }

  @media screen and (max-width: 588px) {
    display: flex;
    flex-direction: column;
    position: relative;
    margin: auto;
    p {
      text-align: left;
    }
    &:after {
      content: "";
      border: 1px dashed #d8d8d8;
      position: absolute;
      top: 38px;
      bottom: 40px;
      left: 28px;
      z-index: -1;
    }
  }
`;

const LIArea = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  gap: 16px;
  flex-basis: 27%;
  border-radius: 24px;
  position: relative;
  &:last-child span:after {
    display: none;
  }

  @media screen and (max-width: 588px) {
    flex-direction: row;
    span {
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
    }
    p {
      width: 100%;
      align-items: center;
    }
    span:after {
      display: none;
    }
  }
`;

const NumberArea = styled.span`
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  display: flex;
  align-items: center;
  text-align: center;
  background: #000;
  color: #fff;
  width: 45px;
  height: 45px;
  justify-content: center;
  border-radius: 50%;
  &:after {
    content: "...";
    position: absolute;
    font-size: 72px;
    top: 10px;
    right: -18px;
    z-index: -1;
    width: 32%;
    letter-spacing: 48px;
  }
  @media screen and (max-width: 588px) {
    position: relative;
    justify-content: flex-start;
    align-items: self-end;
    width: 28.4px;
    height: 24px;
    font-size: 16px;
    text-align: center;
    margin-top: -20px;
    &:before {
      content: "";
      flex-grow: 2;
      height: 2px;
      width: 100%;
    }
  }
`;

// info styles
const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  max-width: 588px;
  margin: auto;
  align-items: start;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 24px;
  margin-bottom: 24px;
  column-gap: 8px;
  .name {
    font-weight: 600;
  }
  p {
    font-size: 14px;
    color: #434343;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    margin-bottom: 24px;
  }
  @media screen and (min-width: 1024px) {
    margin-bottom: 24px;
    max-width: none;
  }
`;

const PolicyConfirmationContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  padding-bottom: 24px;
  justify-content: space-between;
  border-bottom: 1px solid #f5f5f5;
  & span:nth-child(1) {
    flex-wrap: wrap;
    width: 60%;
    font-weight: 600;
    font-size: 14px;
  }
  & span:nth-child(2) {
    display: flex;
    justify-content: flex-end;
    text-align: right;
    font-weight: 400;
    font-size: 14px;
  }
`;

const descriptionClass = css`
  font-size: 14px;
  font-weight: 600;
`;
const font18 = css`
  font-size: 18px;
`;

export interface FormContainerProps {
  margin?: string;
}

export interface FormHeaderTitleContainerProps {
  marginHorizontal: string;
}
